import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BellIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Bell.js';
import BriefcaseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Briefcase.js';
import ChartIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Chart.js';
import DocumentIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Document.js';
import ProfileIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Profile.js';
import SettingsIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Settings.js';
import UserManagementIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/UserManagement.js';
import {
  NavLink as AtlasNavLink,
  NavSection,
} from '@diligentcorp/atlas-web-component-global-nav/lib/react.js';
import { Divider, SystemPermissionRequired } from 'src/components/atoms';
import {
  AuthorisedCaseImporter,
  RequiredFeatureEnabled,
} from 'src/components/auth';
import { handleErrorToast } from 'src/components/molecules';
import { clearSession } from 'src/creators';
import { useDeleteSessionMutation } from 'src/graphql';
import { useCategoryConfigurationRoute, useIsFeatureEnabled } from 'src/hooks';
import { useNotifications } from 'src/hooks/useNotifications';
import type { AppDispatch } from 'src/store';
import { FeatureFlag, NavbarVariantEnum } from 'src/types';
import * as Intercom from 'src/utils/intercom';
import { NavbarPermissions } from './types';
import { NotificationsList } from './components/NotificationsList';
import { RoutedNavLink } from './components/RoutedNavLink';
import { HbGlobalNavigatorShell } from './HbGlobalNavigatorShell';
import { NavbarProvider, useNavbarContext } from './NavbarProvider';

const PRIVACY_STATEMENT_URL =
  'https://vaultplatform.com/product-privacy-statement-web/?utm_source=reshub';

type Props = { disableUpload: boolean };

const CASES_PATHS = [
  '/cases',
  '/case',
  '/disclosures',
  '/disclosure',
  '/auto-closed-disclosure',
  '/uploadCases',
  '/uploadCasesHistory',
  '/deleted-sanitized-cases',
];
const USERS_PATHS = ['/users', '/uploadUsers'];
const INSIGHTS_PATHS = ['/ethical-health', '/ethics-operations'];
const ADMIN_PATHS = [
  '/tags',
  '/field-management',
  '/configurations',
  '/authentication',
  '/integrations',
  '/categories',
  '/data-retention',
  '/groups',
  '/routing',
];

const NavTree = ({ disableUpload }: Props) => {
  const history = useHistory();
  const { pathname } = history.location;
  const [openCases, setOpenCases] = useState(() =>
    CASES_PATHS.some((p) => pathname.startsWith(p)),
  );
  const [openUsers, setOpenUsers] = useState(() =>
    USERS_PATHS.some((p) => pathname.startsWith(p)),
  );
  const [openInsights, setOpenInsights] = useState(() =>
    INSIGHTS_PATHS.some((p) => pathname.startsWith(p)),
  );
  const [openAdmin, setOpenAdmin] = useState(() =>
    ADMIN_PATHS.some((p) => pathname.startsWith(p)),
  );
  const [openAccount, setOpenAccount] = useState(false);
  const categoryConfigurationRoute = useCategoryConfigurationRoute();
  const { isSecondaryDrawerOpen, setSecondaryDrawerOpen } = useNavbarContext();
  const isSpeakUpEnabled = useIsFeatureEnabled(FeatureFlag.SPEAK_UP);
  const isCoiEnabled = useIsFeatureEnabled(FeatureFlag.CONFLICT_OF_INTEREST);
  const showCaseImportDropdown = AuthorisedCaseImporter();
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const logout = useDeleteSessionMutation({
    onSuccess: (data) => {
      queryClient.removeQueries();
      const d1pLogoutUrl = data?.deleteSession?.d1pLogoutUrl;
      if (d1pLogoutUrl) {
        window.location.href = d1pLogoutUrl;
        return;
      }
      dispatch(clearSession());
      history.replace('/login');
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Could not delete session.' });
    },
  });

  const handleLogout = () => {
    Intercom.shutdown();
    logout.mutate({});
    Intercom.boot();
  };

  const handlePrivacyStatement = () => {
    window.open(PRIVACY_STATEMENT_URL, '_blank', 'noopener,noreferrer');
  };

  const handleTechnicalSupport = () => {
    if (Intercom.isOpen) {
      Intercom.hide();
    } else {
      Intercom.show();
    }
  };

  return (
    <>
      <SystemPermissionRequired
        permission={[
          NavbarPermissions.NAV_CASES,
          NavbarPermissions.NAV_DISCLOSURES,
        ]}
      >
        <NavSection
          label="Cases"
          isOpen={openCases}
          isHighlighted={openCases}
          onOpen={() => setOpenCases(true)}
          onClose={() => setOpenCases(false)}
          data-testid="cases-navbar"
        >
          <BriefcaseIcon slot="icon" aria-hidden />
          {isSpeakUpEnabled && (
            <SystemPermissionRequired permission={NavbarPermissions.NAV_CASES}>
              <RoutedNavLink
                to="/cases"
                label="Manage Cases"
                data-testid="cases-link-navbar"
              />
            </SystemPermissionRequired>
          )}
          {isCoiEnabled && (
            <SystemPermissionRequired
              permission={NavbarPermissions.NAV_DISCLOSURES}
            >
              <RoutedNavLink
                to="/disclosures"
                label="Manage Disclosures"
                data-testid="coi-disclosures"
              />
            </SystemPermissionRequired>
          )}
          {showCaseImportDropdown && (
            <RoutedNavLink
              to="/uploadCases"
              label="Import Cases"
              data-testid="import-cases-link-navbar"
            />
          )}
          {showCaseImportDropdown && (
            <RoutedNavLink
              to="/uploadCasesHistory"
              label="Import History"
              data-testid="import-history-link-navbar"
            />
          )}
          <SystemPermissionRequired permission={NavbarPermissions.NAV_ADMIN}>
            <RequiredFeatureEnabled
              requiredFeature={FeatureFlag.DATA_RETENTION}
            >
              <RoutedNavLink
                to="/deleted-sanitized-cases"
                label="Deleted/sanitized cases"
                data-testid="deleted-cases-link-navbar"
              />
            </RequiredFeatureEnabled>
          </SystemPermissionRequired>
        </NavSection>
      </SystemPermissionRequired>

      <SystemPermissionRequired permission={NavbarPermissions.NAV_ADMIN}>
        <RequiredFeatureEnabled
          requiredFeature={[
            FeatureFlag.DYNAMIC_QUESTIONS_V1,
            FeatureFlag.CONFLICT_OF_INTEREST,
          ]}
        >
          <RoutedNavLink
            to="/form-management"
            label="Forms"
            data-testid="form-management-navbar"
          >
            <DocumentIcon slot="icon" aria-hidden />
          </RoutedNavLink>
        </RequiredFeatureEnabled>
      </SystemPermissionRequired>

      <SystemPermissionRequired permission={NavbarPermissions.NAV_USERS}>
        {!disableUpload ? (
          <NavSection
            label="Users"
            isOpen={openUsers}
            isHighlighted={openUsers}
            onOpen={() => setOpenUsers(true)}
            onClose={() => setOpenUsers(false)}
            data-testid="users-dropdown-navbar"
          >
            <UserManagementIcon slot="icon" aria-hidden />
            <RoutedNavLink
              to="/users"
              label="Manage Users"
              data-testid="users-link-navbar"
            />
            <RoutedNavLink
              to="/uploadUsers"
              label="Upload Users"
              data-testid="upload-users-link-navbar"
            />
          </NavSection>
        ) : (
          <RoutedNavLink to="/users" label="Users" data-testid="users-navbar">
            <UserManagementIcon slot="icon" aria-hidden />
          </RoutedNavLink>
        )}
      </SystemPermissionRequired>

      {isSpeakUpEnabled && (
        <SystemPermissionRequired permission={NavbarPermissions.NAV_INSIGHTS}>
          <NavSection
            label="Insights"
            isOpen={openInsights}
            isHighlighted={openInsights}
            onOpen={() => setOpenInsights(true)}
            onClose={() => setOpenInsights(false)}
            data-testid="insights-dropdown-navbar"
          >
            <ChartIcon slot="icon" aria-hidden />
            <RoutedNavLink
              to="/ethical-health"
              label="Ethical Health"
              data-testid="ethical-health-link-navbar"
            />
            <RoutedNavLink
              to="/ethics-operations"
              label="Ethics Operations"
              data-testid="ethics-operations-link-navbar"
            />
          </NavSection>
        </SystemPermissionRequired>
      )}

      <SystemPermissionRequired permission={NavbarPermissions.NAV_ADMIN}>
        <NavSection
          label="Administration"
          isOpen={openAdmin}
          isHighlighted={openAdmin}
          onOpen={() => setOpenAdmin(true)}
          onClose={() => setOpenAdmin(false)}
          data-testid="admin-dropdown-navbar"
        >
          <SettingsIcon slot="icon" aria-hidden />
          <RoutedNavLink
            to="/tags"
            label="Tags"
            data-testid="settings-link-navbar-tags"
          />
          <RequiredFeatureEnabled requiredFeature={FeatureFlag.CUSTOM_FIELDS}>
            <RoutedNavLink
              to="/field-management"
              label="Field management"
              data-testid="settings-link-navbar-custom-fields"
            />
          </RequiredFeatureEnabled>
          <RequiredFeatureEnabled
            requiredFeature={[
              FeatureFlag.PUBLIC_REPORTING,
              FeatureFlag.VAULT_TALK,
              FeatureFlag.HRIS_SYNC,
              FeatureFlag.OPR_DEFAULT_ASSIGNEE_ROUTING,
              FeatureFlag.SLACK_REPORTING,
            ]}
          >
            <RoutedNavLink
              to="/configurations"
              label="Configurations"
              data-testid="settings-link-navbar-configurations"
            />
          </RequiredFeatureEnabled>
          <RoutedNavLink
            to="/authentication"
            label="Authentication"
            data-testid="settings-link-navbar-authentication"
          />
          <RequiredFeatureEnabled
            requiredFeature={FeatureFlag.DATA_INTEGRATION}
          >
            <RoutedNavLink
              to="/integrations"
              label="Data Integration"
              data-testid="settings-link-navbar-integrations"
            />
          </RequiredFeatureEnabled>
          <RoutedNavLink
            to={categoryConfigurationRoute}
            label="Category Configuration"
            data-testid="settings-link-navbar-categories"
          />
          <RequiredFeatureEnabled requiredFeature={FeatureFlag.DATA_RETENTION}>
            <RoutedNavLink
              to="/data-retention"
              label="Data Retention"
              data-testid="settings-link-navbar-data-retention"
            />
          </RequiredFeatureEnabled>
          <RequiredFeatureEnabled requiredFeature={FeatureFlag.GROUPS}>
            <RoutedNavLink
              to="/groups"
              label="Manage Groups"
              data-testid="groups-link-navbar"
            />
          </RequiredFeatureEnabled>
          <RequiredFeatureEnabled requiredFeature={FeatureFlag.ROUTING}>
            <RoutedNavLink
              to="/routing"
              label="Routing"
              data-testid="settings-link-navbar-routing"
            />
          </RequiredFeatureEnabled>
        </NavSection>
      </SystemPermissionRequired>

      <Divider variant="horizontal" data-testid="admin-section-divider" />

      <NavSection
        label="Account"
        isOpen={openAccount}
        isHighlighted={openAccount}
        onOpen={() => setOpenAccount(true)}
        onClose={() => setOpenAccount(false)}
        data-testid="account-dropdown-navbar"
      >
        <ProfileIcon slot="icon" aria-hidden />
        <RoutedNavLink
          to="/case-settings"
          label="Settings"
          data-testid="case-settings-link-navbar"
        />
        <RoutedNavLink
          to="/change-password"
          label="Change password"
          data-testid="change-password-link-navbar"
        />
        <AtlasNavLink
          as="button"
          label="Privacy statement"
          data-testid="privacy-statement-link-navbar"
          onClick={handlePrivacyStatement}
        />
        <AtlasNavLink
          as="button"
          label="Technical Support"
          data-testid="technical-support-link-navbar"
          onClick={handleTechnicalSupport}
        />
        <AtlasNavLink
          as="button"
          label="Log out"
          data-testid="logout-link-navbar"
          onClick={handleLogout}
        />
      </NavSection>

      <AtlasNavLink
        as="button"
        label="Notifications"
        data-testid="notifications-link-navbar"
        onClick={() => setSecondaryDrawerOpen(!isSecondaryDrawerOpen)}
      >
        <BellIcon slot="icon" aria-hidden />
      </AtlasNavLink>
    </>
  );
};

export const ReshubNavbarAtlas = ({ disableUpload }: Props) => {
  const { notifications } = useNotifications();

  return (
    <NavbarProvider responsive={false}>
      <HbGlobalNavigatorShell>
        <NavTree disableUpload={disableUpload} />
      </HbGlobalNavigatorShell>
      {/* Rendered as a sibling, not nested in the sidebar subtree, so it can
          use position: fixed; right: 0 — Atlas's layout has no slot for a
          secondary drawer the way BaseNavbar does. Opened by the
          "Notifications" nav link toggling isSecondaryDrawerOpen. */}
      <NotificationsList
        notifications={notifications}
        variant={NavbarVariantEnum.MANAGER}
        atlasVariant
      />
    </NavbarProvider>
  );
};
